import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import GratificacionesDetalle from '../components/GratificacionesDetalle';
import LiquidacionesDetalle from '../components/LiquidacionesDetalle';
import CtsDetalle from '../components/CtsDetalle';
import CerrarCtsConfirmModal from '../components/CerrarCtsConfirmModal';
import CerrarGratificacionConfirmModal from '../components/CerrarGratificacionConfirmModal';
import gratificacionService from '../services/gratificacionService';
import liquidacionService from '../services/liquidacionService';
import ctsService from '../services/ctsService';
import { formatFechaSoloDiaDdMmYyyy } from '../utils/formatFechaSoloDia';

const Calculos = () => {
  const { empresaId } = useParams();
  const { selectedCompany } = useAuth();
  
  const [activeTab, setActiveTab] = useState('gratificaciones');
  const [loading, setLoading] = useState(true);
  const [showGratificacionesDetalle, setShowGratificacionesDetalle] = useState(false);
  const [selectedPeriodoGratificacion, setSelectedPeriodoGratificacion] = useState(null);
  const [showCtsDetalle, setShowCtsDetalle] = useState(false);
  const [selectedPeriodoCts, setSelectedPeriodoCts] = useState(null);

  const [gratificacionesResumen, setGratificacionesResumen] = useState([]);
  const [ctsData, setCtsData] = useState([]);
  const [liquidaciones, setLiquidaciones] = useState([]);

  const [filters, setFilters] = useState({
    estado: 'all',
    dateType: 'all'
  });

  const [showModalNuevaGratificacion, setShowModalNuevaGratificacion] = useState(false);
  const [nuevaGratificacionPeriodo, setNuevaGratificacionPeriodo] = useState('Diciembre');
  const [nuevaGratificacionAnio, setNuevaGratificacionAnio] = useState(new Date().getFullYear());
  const [creandoGratificacion, setCreandoGratificacion] = useState(false);
  const [errorGratificacion, setErrorGratificacion] = useState(null);

  const [showModalNuevaCts, setShowModalNuevaCts] = useState(false);
  const [nuevaCtsPeriodo, setNuevaCtsPeriodo] = useState('Mayo');
  const [nuevaCtsAnio, setNuevaCtsAnio] = useState(new Date().getFullYear());
  const [creandoCts, setCreandoCts] = useState(false);
  const [errorCts, setErrorCts] = useState(null);
  /** Modal único para cerrar CTS desde la lista */
  const [ctsCierreDesdeLista, setCtsCierreDesdeLista] = useState(null);
  const [ctsCierreEnviandoLista, setCtsCierreEnviandoLista] = useState(false);
  /** Modal para cerrar gratificación (incluir en planilla sí/no) */
  const [gratCierreDesdeLista, setGratCierreDesdeLista] = useState(null);
  const [gratCierreEnviandoLista, setGratCierreEnviandoLista] = useState(false);

  const tabs = [
    { id: 'gratificaciones', name: 'Gratificaciones', icon: '🎁', color: 'text-green-600' },
    { id: 'cts', name: 'CTS', icon: '🏦', color: 'text-blue-600' },
    { id: 'liquidaciones', name: 'Liquidaciones', icon: '📋', color: 'text-red-600' }
  ];

  const idEmpresa = empresaId || selectedCompany?.id;

  const loadGratificaciones = useCallback(async () => {
    if (!idEmpresa) {
      setGratificacionesResumen([]);
      return;
    }
    try {
      const res = await gratificacionService.list(idEmpresa);
      const list = (res.data || []).map((g) => ({
        id: g.id,
        gratificacionId: g.id,
        año: g.anio,
        periodo: g.periodo_nombre || (g.periodo === 'junio' ? 'Junio' : 'Diciembre'),
        nombre: g.nombre,
        descripcion: g.descripcion || '',
        fechaInicio: g.fecha_inicio,
        fechaFin: g.fecha_fin,
        totalEmpleados: g.total_empleados ?? 0,
        totalCalculado: g.total_calculado ?? 0,
        totalCalculadoInterna: g.total_calculado_interna ?? 0,
        planillaAlternativaActiva: Boolean(g.planilla_alternativa_activa),
        estado: g.estado === 'cerrada' ? 'cerrada' : 'proceso',
        entrara_en_planilla:
          g.entrara_en_planilla === true || g.entrara_en_planilla === false ? g.entrara_en_planilla : null,
      }));
      setGratificacionesResumen(list);
    } catch (err) {
      console.error('Error cargando gratificaciones:', err);
      setGratificacionesResumen([]);
    }
  }, [idEmpresa]);

  const loadLiquidaciones = useCallback(async () => {
    if (!idEmpresa) {
      setLiquidaciones([]);
      return;
    }
    try {
      const res = await liquidacionService.list(idEmpresa);
      setLiquidaciones(res.data || []);
    } catch (err) {
      console.error('Error cargando liquidaciones:', err);
      setLiquidaciones([]);
    }
  }, [idEmpresa]);

  const loadCts = useCallback(async () => {
    if (!idEmpresa) {
      setCtsData([]);
      return;
    }
    try {
      const res = await ctsService.list(idEmpresa);
      const raw = Array.isArray(res?.data) ? res.data : [];
      const list = raw.map((g) => ({
        id: g.id,
        ctsId: g.id,
        año: g.anio,
        periodo: g.periodo_nombre || (g.periodo === 'mayo' ? 'Mayo' : 'Noviembre'),
        nombre: g.nombre,
        descripcion: g.descripcion || '',
        fechaInicio: g.fecha_inicio,
        fechaFin: g.fecha_fin,
        totalEmpleados: g.total_empleados ?? 0,
        totalCalculado: g.total_calculado ?? 0,
        estado: g.estado === 'cerrada' ? 'cerrada' : 'proceso',
        entrara_en_planilla: g.entrara_en_planilla === true || g.entrara_en_planilla === false ? g.entrara_en_planilla : null,
      }));
      setCtsData(list);
    } catch (err) {
      console.error('Error cargando CTS:', err);
      setCtsData([]);
    }
  }, [idEmpresa]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      if (idEmpresa) {
        await loadGratificaciones();
        await loadLiquidaciones();
        await loadCts();
      } else {
        setGratificacionesResumen([]);
        setLiquidaciones([]);
        setCtsData([]);
      }
      setLoading(false);
    };
    loadData();
  }, [idEmpresa, loadGratificaciones, loadLiquidaciones, loadCts]);

  // Al cambiar a la pestaña Liquidaciones, refrescar lista para que las estadísticas estén al día
  useEffect(() => {
    if (idEmpresa && activeTab === 'liquidaciones') loadLiquidaciones();
  }, [idEmpresa, activeTab, loadLiquidaciones]);

  useEffect(() => {
    if (idEmpresa && activeTab === 'cts') loadCts();
  }, [idEmpresa, activeTab, loadCts]);

  useEffect(() => {
    if (activeTab !== 'cts') {
      setCtsCierreDesdeLista(null);
      setShowCtsDetalle(false);
      setSelectedPeriodoCts(null);
    }
  }, [activeTab]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  /** Con planilla alternativa: mostrar total PLAME + Alternativa en gratificaciones. */
  const renderTotalesDocumento = (documento, { compact = false } = {}) => {
    const esGrat = activeTab === 'gratificaciones';
    const conAlternativa = esGrat && documento.planillaAlternativaActiva;
    if (!conAlternativa) {
      return (
        <span className={compact ? 'font-bold text-green-600' : 'text-sm font-bold text-green-600'}>
          {formatCurrency(documento.totalCalculado)}
        </span>
      );
    }
    return (
      <div className={compact ? 'space-y-0.5' : 'space-y-1'}>
        <div className={compact ? 'text-sm' : 'text-sm font-bold text-green-600'}>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mr-1">PLAME</span>
          <span className="font-bold text-green-600">{formatCurrency(documento.totalCalculado)}</span>
        </div>
        <div className={compact ? 'text-sm' : 'text-sm font-bold text-amber-700'}>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600/80 mr-1">Alt.</span>
          <span className="font-bold text-amber-700">{formatCurrency(documento.totalCalculadoInterna ?? 0)}</span>
        </div>
      </div>
    );
  };

  // Fechas de API (YYYY-MM-DD / ISO): formato sin desfase por zona horaria local
  const formatDateToDDMMYYYY = (dateString) => formatFechaSoloDiaDdMmYyyy(dateString, 'N/A');

  const getEstadoBadge = (estado) => {
    const estadoConfig = {
      proceso: { class: 'bg-blue-100 text-blue-800', text: 'En Proceso', icon: '⚙️' },
      cerrada: { class: 'bg-green-100 text-green-800', text: 'Cerrada', icon: '✅' },
      anulada: { class: 'bg-red-100 text-red-800', text: 'Anulada', icon: '❌' }
    };
    return estadoConfig[estado] || estadoConfig.proceso;
  };

  const handleVerDetalleGratificacion = (doc) => {
    const gid = doc.gratificacionId ?? doc.id;
    setSelectedPeriodoGratificacion({
      ...doc,
      id: `grat-${gid}`,
      gratificacionId: gid,
    });
    setShowGratificacionesDetalle(true);
  };

  const handleVolverFromDetalle = () => {
    setShowGratificacionesDetalle(false);
    setSelectedPeriodoGratificacion(null);
  };

  const abrirModalCierreCtsDesdeLista = (documento) => {
    setCtsCierreDesdeLista({ id: documento.id, nombre: documento.nombre });
  };

  const handleConfirmarCierreCtsDesdeLista = async (entraraPlanilla) => {
    if (!ctsCierreDesdeLista?.id) return;
    setCtsCierreEnviandoLista(true);
    try {
      const res = await ctsService.cerrar(ctsCierreDesdeLista.id, entraraPlanilla);
      setCtsCierreDesdeLista(null);
      await loadCts();
      alert(res.message || 'CTS cerrado exitosamente');
    } catch (error) {
      console.error('Error al cerrar cts:', error);
      alert(error.response?.data?.message || 'Error al cerrar CTS');
    } finally {
      setCtsCierreEnviandoLista(false);
    }
  };

  const abrirModalCierreGratDesdeLista = (documento) => {
    setGratCierreDesdeLista({
      id: documento.gratificacionId ?? documento.id,
      nombre: documento.nombre,
    });
  };

  const handleConfirmarCierreGratDesdeLista = async (entraraPlanilla) => {
    if (!gratCierreDesdeLista?.id) return;
    setGratCierreEnviandoLista(true);
    try {
      const res = await gratificacionService.cerrar(gratCierreDesdeLista.id, entraraPlanilla);
      setGratCierreDesdeLista(null);
      await loadGratificaciones();
      alert(res.message || 'Gratificación cerrada exitosamente');
    } catch (error) {
      console.error('Error al cerrar gratificación:', error);
      alert(error.response?.data?.message || 'Error al cerrar gratificación');
    } finally {
      setGratCierreEnviandoLista(false);
    }
  };

  const handleCerrarDocumento = async (id, tipo) => {
    try {
      if (tipo === 'liquidacion') {
        setLiquidaciones(prev => prev.map(item => 
          item.id === id ? { ...item, estado: 'cerrada' } : item
        ));
        alert('Liquidación cerrada exitosamente');
      }
    } catch (error) {
      console.error(`Error al cerrar ${tipo}:`, error);
      alert(error.response?.data?.message || `Error al cerrar ${tipo}`);
    }
  };

  const handleBorrarGratificacion = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar la gratificación "${nombre || 'esta gratificación'}"? Esta acción no se puede deshacer.`)) return;
    try {
      await gratificacionService.delete(id);
      if (selectedPeriodoGratificacion?.gratificacionId === id) {
        setShowGratificacionesDetalle(false);
        setSelectedPeriodoGratificacion(null);
      }
      await loadGratificaciones();
      alert('Gratificación eliminada correctamente');
    } catch (error) {
      console.error('Error al eliminar gratificación:', error);
      alert(error.response?.data?.message || 'Error al eliminar la gratificación');
    }
  };

  const handleBorrarCts = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar el CTS "${nombre || 'este CTS'}"? Solo se puede si está en proceso.`)) return;
    try {
      await ctsService.delete(id);
      if ((selectedPeriodoCts?.ctsId ?? selectedPeriodoCts?.id) === id) {
        setShowCtsDetalle(false);
        setSelectedPeriodoCts(null);
      }
      await loadCts();
      alert('CTS eliminado correctamente');
    } catch (error) {
      console.error('Error al eliminar CTS:', error);
      alert(error.response?.data?.message || 'Error al eliminar el CTS');
    }
  };

  const handleVerDetalleCts = (doc) => {
    setSelectedPeriodoCts({
      ...doc,
      ctsId: doc.ctsId ?? doc.id,
    });
    setShowCtsDetalle(true);
  };

  const handleVolverFromCtsDetalle = () => {
    setShowCtsDetalle(false);
    setSelectedPeriodoCts(null);
  };

  const handleAbrirDocumento = async (id, tipo) => {
    try {
      if (tipo === 'gratificacion') {
        await gratificacionService.abrir(id);
        await loadGratificaciones();
        alert('Gratificación reabierta exitosamente');
      } else if (tipo === 'cts') {
        await ctsService.abrir(id);
        await loadCts();
        alert('CTS reabierto exitosamente');
      } else if (tipo === 'liquidacion') {
        setLiquidaciones(prev => prev.map(item => 
          item.id === id ? { ...item, estado: 'proceso' } : item
        ));
        alert('Liquidación reabierta exitosamente');
      }
    } catch (error) {
      console.error(`Error al abrir ${tipo}:`, error);
      alert(error.response?.data?.message || `Error al abrir ${tipo}`);
    }
  };

  const handleCrearGratificacion = async () => {
    if (!idEmpresa) return;
    setCreandoGratificacion(true);
    setErrorGratificacion(null);
    try {
      const res = await gratificacionService.create(idEmpresa, nuevaGratificacionPeriodo, nuevaGratificacionAnio);
      setShowModalNuevaGratificacion(false);
      await loadGratificaciones();
      if (res.data?.id) {
        const g = res.data;
        setSelectedPeriodoGratificacion({
          gratificacionId: g.id,
          id: `grat-${g.id}`,
          periodo: g.periodo_nombre || (g.periodo === 'junio' ? 'Junio' : 'Diciembre'),
          año: g.anio,
          nombre: g.nombre,
          fechaInicio: g.fecha_inicio,
          fechaFin: g.fecha_fin,
          totalEmpleados: g.total_empleados,
          totalCalculado: g.total_calculado,
          estado: g.estado === 'cerrada' ? 'cerrada' : 'proceso',
        });
        setShowGratificacionesDetalle(true);
      }
      const aviso = res.aviso || res.message || 'Gratificación creada correctamente';
      const totalEmp = res.data?.total_empleados ?? 0;
      if (totalEmp === 0) {
        alert(`⚠️ ${aviso}\n\nPara Junio se necesitan planillas cerradas ene–jun.\nPara Diciembre se necesitan planillas cerradas jul–dic.`);
      } else {
        alert(`Gratificación creada correctamente (${totalEmp} empleados).`);
      }
    } catch (err) {
      console.error('Error creando gratificación:', err);
      setErrorGratificacion(err.response?.data?.message || 'Error al crear gratificación');
    } finally {
      setCreandoGratificacion(false);
    }
  };

  const handleCrearCts = async () => {
    if (!idEmpresa) return;
    setCreandoCts(true);
    setErrorCts(null);
    try {
      const res = await ctsService.create(idEmpresa, nuevaCtsPeriodo, nuevaCtsAnio);
      setShowModalNuevaCts(false);
      await loadCts();
      if (res.data?.id) {
        const g = res.data;
        setSelectedPeriodoCts({
          ctsId: g.id,
          id: g.id,
          periodo: g.periodo_nombre || (g.periodo === 'mayo' ? 'Mayo' : 'Noviembre'),
          año: g.anio,
          nombre: g.nombre,
          fechaInicio: g.fecha_inicio,
          fechaFin: g.fecha_fin,
          totalEmpleados: g.total_empleados,
          totalCalculado: g.total_calculado,
          estado: g.estado === 'cerrada' ? 'cerrada' : 'proceso',
        });
        setShowCtsDetalle(true);
      }
      alert(res.message || 'CTS creado correctamente');
    } catch (err) {
      console.error('Error creando CTS:', err);
      setErrorCts(err.response?.data?.message || 'Error al crear CTS');
    } finally {
      setCreandoCts(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Obtener datos filtrados según tab activo
  const getCurrentData = () => {
    let data = [];
    if (activeTab === 'gratificaciones') data = gratificacionesResumen;
    else if (activeTab === 'cts') data = ctsData;
    else if (activeTab === 'liquidaciones') data = liquidaciones;
    
    return data.filter(item => {
      if (filters.estado === 'all') return true;
      return item.estado === filters.estado;
    });
  };

  const filteredData = getCurrentData();

  // Estadísticas adaptadas por tab
  const getStats = () => {
    if (activeTab === 'gratificaciones') {
      const d = gratificacionesResumen;
      return {
        tipo: 'gratificaciones',
        total: d.length,
        proceso: d.filter(p => p.estado === 'proceso').length,
        cerrada: d.filter(p => p.estado === 'cerrada').length,
        montoTotal: d.reduce((s, p) => s + (Number(p.totalCalculado) || 0), 0),
      };
    }
    if (activeTab === 'cts') {
      const d = ctsData;
      return {
        tipo: 'cts',
        total: d.length,
        proceso: d.filter(p => p.estado === 'proceso').length,
        cerrada: d.filter(p => p.estado === 'cerrada').length,
        montoTotal: d.reduce((s, p) => s + (Number(p.totalCalculado) || 0), 0),
      };
    }
    // Liquidaciones: no tienen estado proceso/cerrada, son registros generados
    const d = liquidaciones;
    const anioActual = new Date().getFullYear();
    return {
      tipo: 'liquidaciones',
      total: d.length,
      montoTotal: d.reduce((s, p) => s + (Number(p.total_liquidacion) || 0), 0),
      esteAnio: d.filter(p => {
        const f = p.fecha_generacion || p.created_at;
        return f && new Date(f).getFullYear() === anioActual;
      }).length,
    };
  };

  const stats = getStats();

  // Si no hay empresa seleccionada
  if (!selectedCompany) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empresa seleccionada</h3>
          <p className="text-gray-500 text-center">
            Selecciona una empresa para ver los cálculos laborales.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 lg:space-y-4 px-1 sm:px-0">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between lg:gap-8 space-y-4 lg:space-y-0">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">🧮 Cálculos Laborales</h1>
          <p className="text-gray-600">Gestión de gratificaciones, CTS y liquidaciones</p>
          {selectedCompany && (
            <p className="text-sm text-gray-500">Empresa: {selectedCompany.nombre_empresa}</p>
          )}
        </div>
        <button
          onClick={() => {
            if (activeTab === 'gratificaciones') setShowModalNuevaGratificacion(true);
            if (activeTab === 'cts') {
              setErrorCts(null);
              setShowModalNuevaCts(true);
            }
          }}
          className={`flex items-center justify-center space-x-2 px-5 py-2.5 text-white rounded-lg font-medium transition-all duration-200 disabled:opacity-50 shrink-0 ${
            activeTab === 'cts'
              ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700'
              : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
          }`}
          disabled={(activeTab !== 'gratificaciones' && activeTab !== 'cts') || !idEmpresa}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Nuevo Cálculo</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? `${tab.color} border-current`
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Statistics Cards — adaptadas por pestaña */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
        {stats.tipo === 'liquidaciones' ? (
          <>
            <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center">
                <div className="p-2 bg-red-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-xs lg:text-sm font-medium text-gray-600">Liquidaciones generadas</p>
                  <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center">
                <div className="p-2 bg-green-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-xs lg:text-sm font-medium text-gray-600">Monto total liquidado</p>
                  <p className="text-lg lg:text-2xl font-bold text-green-600">{formatCurrency(stats.montoTotal)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200 sm:col-span-2 lg:col-span-1">
              <div className="flex flex-col lg:flex-row lg:items-center">
                <div className="p-2 bg-amber-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-xs lg:text-sm font-medium text-gray-600">Generadas este año</p>
                  <p className="text-lg lg:text-2xl font-bold text-amber-600">{stats.esteAnio}</p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center">
                <div
                  className={`p-2 rounded-lg mb-2 lg:mb-0 lg:mr-4 ${
                    stats.tipo === 'cts' ? 'bg-emerald-100' : 'bg-blue-100'
                  }`}
                >
                  <svg
                    className={`w-5 h-5 lg:w-6 lg:h-6 ${
                      stats.tipo === 'cts' ? 'text-emerald-600' : 'text-blue-600'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-xs lg:text-sm font-medium text-gray-600">
                    {stats.tipo === 'gratificaciones' ? 'Periodos de gratificación' : 'Períodos CTS'}
                  </p>
                  <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center">
                <div
                  className={`p-2 rounded-lg mb-2 lg:mb-0 lg:mr-4 ${
                    stats.tipo === 'cts' ? 'bg-teal-100' : 'bg-blue-100'
                  }`}
                >
                  <svg
                    className={`w-5 h-5 lg:w-6 lg:h-6 ${
                      stats.tipo === 'cts' ? 'text-teal-600' : 'text-blue-600'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-xs lg:text-sm font-medium text-gray-600">En proceso</p>
                  <p
                    className={`text-lg lg:text-2xl font-bold ${
                      stats.tipo === 'cts' ? 'text-teal-600' : 'text-blue-600'
                    }`}
                  >
                    {stats.proceso}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center">
                <div className="p-2 bg-green-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-xs lg:text-sm font-medium text-gray-600">Cerradas</p>
                  <p className="text-lg lg:text-2xl font-bold text-green-600">{stats.cerrada}</p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Filtros — solo para Gratificaciones y CTS (listas con estado) */}
      {activeTab !== 'liquidaciones' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 lg:p-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
              <select
                value={filters.estado}
                onChange={(e) => setFilters(prev => ({ ...prev, estado: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="all">Todos los estados</option>
                <option value="proceso">En Proceso</option>
                <option value="cerrada">Cerrada</option>
                <option value="anulada">Anulada</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filtrar por</label>
              <select
                value={filters.dateType}
                onChange={(e) => setFilters(prev => ({ ...prev, dateType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="all">Todos</option>
                <option value="year">Por Año</option>
                <option value="month">Por Mes</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ estado: 'all', dateType: 'all' })}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Limpiar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Documentos */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 lg:p-6">
        {showGratificacionesDetalle ? (
          <GratificacionesDetalle
            empresaId={empresaId || selectedCompany?.id}
            selectedPeriodo={selectedPeriodoGratificacion}
            onVolver={handleVolverFromDetalle}
            onGratificacionActualizada={async () => {
              await loadGratificaciones();
              setSelectedPeriodoGratificacion((prev) => {
                if (!prev) return prev;
                const gid = prev.gratificacionId ?? prev.id;
                return {
                  ...prev,
                  id: typeof prev.id === 'string' && String(prev.id).startsWith('grat-')
                    ? prev.id
                    : `grat-${gid}`,
                  gratificacionId: gid,
                  estado: 'cerrada',
                };
              });
            }}
          />
        ) : showCtsDetalle && activeTab === 'cts' ? (
          <CtsDetalle
            selectedPeriodo={selectedPeriodoCts}
            onVolver={handleVolverFromCtsDetalle}
            onCtsUpdated={loadCts}
          />
        ) : activeTab === 'liquidaciones' ? (
          <LiquidacionesDetalle
            empresaId={empresaId || selectedCompany?.id}
            onVolver={() => setActiveTab('gratificaciones')}
            onLiquidacionesChange={loadLiquidaciones}
          />
        ) : (
          <>
            {filteredData.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay documentos</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Comienza creando un nuevo {activeTab === 'gratificaciones' ? 'cálculo de gratificaciones' : 
                                            activeTab === 'cts' ? 'cálculo de CTS' : 
                                            'cálculo de liquidación'}.
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => {
                      if (activeTab === 'gratificaciones') setShowModalNuevaGratificacion(true);
                      if (activeTab === 'cts') setShowModalNuevaCts(true);
                    }}
                    disabled={activeTab !== 'gratificaciones' && activeTab !== 'cts'}
                    className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white disabled:opacity-50 ${
                      activeTab === 'cts' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Nuevo Cálculo
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Documento
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Período
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Empleados
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredData.map((documento) => {
                        const estadoConfig = getEstadoBadge(documento.estado);
                        const esCts = activeTab === 'cts';
                        const avatarGradient = esCts
                          ? 'from-green-500 to-green-600'
                          : 'from-blue-500 to-blue-600';
                        const chipEmpleados = esCts
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-blue-100 text-blue-800';
                        return (
                          <tr key={documento.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className={`w-10 h-10 bg-gradient-to-br ${avatarGradient} rounded-full flex items-center justify-center`}>
                                  <span className="text-white font-bold text-sm">
                                    {documento.nombre.charAt(0)}
                                  </span>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">{documento.nombre}</div>
                                  <div className="text-xs text-gray-500">{documento.descripcion}</div>
                                  {documento.estado === 'cerrada' && typeof documento.entrara_en_planilla === 'boolean' ? (
                                    <div className="text-[11px] text-teal-800 mt-0.5 font-medium">
                                      {documento.entrara_en_planilla
                                        ? esCts
                                          ? 'Planilla (al cierre): ingreso CTS declarado'
                                          : 'Planilla (al cierre): incluir en planilla'
                                        : 'Planilla (al cierre): no incluir en planilla'}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>{formatDateToDDMMYYYY(documento.fechaInicio)}</div>
                              <div className="text-xs text-gray-500">{formatDateToDDMMYYYY(documento.fechaFin)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${chipEmpleados}`}>
                                {documento.totalEmpleados} empleados
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {renderTotalesDocumento(documento)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${estadoConfig.class}`}>
                                <span className="mr-1">{estadoConfig.icon}</span>
                                {estadoConfig.text}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex space-x-2">
                                {(activeTab === 'gratificaciones' || activeTab === 'cts') && (
                                  <button
                                    onClick={() =>
                                      activeTab === 'gratificaciones'
                                        ? handleVerDetalleGratificacion(documento)
                                        : handleVerDetalleCts(documento)
                                    }
                                    className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                                      activeTab === 'cts'
                                        ? 'bg-green-50 text-green-700 hover:bg-green-100'
                                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                    }`}
                                    title="Ver Detalle"
                                  >
                                    Ver Detalle
                                  </button>
                                )}
                                {documento.estado === 'proceso' && (
                                  <button
                                    onClick={() =>
                                      activeTab === 'cts'
                                        ? abrirModalCierreCtsDesdeLista(documento)
                                        : activeTab === 'gratificaciones'
                                          ? abrirModalCierreGratDesdeLista(documento)
                                          : handleCerrarDocumento(documento.id, 'liquidacion')
                                    }
                                    className="px-3 py-1 text-xs font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                                    title="Cerrar Documento"
                                  >
                                    Cerrar
                                  </button>
                                )}
                                {documento.estado === 'cerrada' && (
                                    <button
                                    onClick={() => handleAbrirDocumento(documento.id, activeTab === 'gratificaciones' ? 'gratificacion' : activeTab === 'cts' ? 'cts' : 'liquidacion')}
                                    className="px-3 py-1 text-xs font-medium rounded-lg bg-yellow-50 text-yellow-700 hover:bg-yellow-100 transition-colors"
                                    title="Abrir Documento"
                                  >
                                    Abrir
                                  </button>
                                )}
                                {activeTab === 'gratificaciones' && documento.estado === 'proceso' && (
                                  <button
                                    onClick={() => handleBorrarGratificacion(documento.id, documento.nombre)}
                                    className="px-3 py-1 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                                    title="Borrar gratificación"
                                  >
                                    Borrar
                                  </button>
                                )}
                                {activeTab === 'cts' && documento.estado === 'proceso' && (
                                  <button
                                    onClick={() => handleBorrarCts(documento.id, documento.nombre)}
                                    className="px-3 py-1 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                                    title="Borrar CTS"
                                  >
                                    Borrar
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden divide-y divide-gray-200">
                  {filteredData.map((documento) => {
                    const estadoConfig = getEstadoBadge(documento.estado);
                    const esCtsM = activeTab === 'cts';
                    const avatarGradientM = esCtsM ? 'from-green-500 to-green-600' : 'from-blue-500 to-blue-600';
                    return (
                      <div key={documento.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className={`w-10 h-10 bg-gradient-to-br ${avatarGradientM} rounded-full flex items-center justify-center`}>
                              <span className="text-white font-bold text-sm">
                                {documento.nombre.charAt(0)}
                              </span>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">{documento.nombre}</div>
                              <div className="text-xs text-gray-500">{documento.descripcion}</div>
                              {documento.estado === 'cerrada' && typeof documento.entrara_en_planilla === 'boolean' ? (
                                <div className="text-[11px] text-teal-800 mt-0.5 font-medium">
                                  {documento.entrara_en_planilla
                                    ? esCtsM
                                      ? 'Planilla (al cierre): ingreso CTS declarado'
                                      : 'Planilla (al cierre): incluir en planilla'
                                    : 'Planilla (al cierre): no incluir en planilla'}
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${estadoConfig.class}`}>
                            <span className="mr-1">{estadoConfig.icon}</span>
                            {estadoConfig.text}
                          </span>
                        </div>
                        
                        <div className="mt-3 text-sm text-gray-600">
                          <div>Período: {formatDateToDDMMYYYY(documento.fechaInicio)} - {formatDateToDDMMYYYY(documento.fechaFin)}</div>
                          <div className="mt-1">Empleados: {documento.totalEmpleados}</div>
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <span className="text-gray-500">Total:</span>
                            {renderTotalesDocumento(documento, { compact: true })}
                          </div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">
                          {(activeTab === 'gratificaciones' || activeTab === 'cts') && (
                            <button
                              onClick={() =>
                                activeTab === 'gratificaciones'
                                  ? handleVerDetalleGratificacion(documento)
                                  : handleVerDetalleCts(documento)
                              }
                              className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                                activeTab === 'cts'
                                  ? 'bg-green-50 text-green-700 hover:bg-green-100'
                                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                              }`}
                            >
                              Ver Detalle
                            </button>
                          )}
                          {documento.estado === 'proceso' && (
                            <button
                              onClick={() =>
                                activeTab === 'cts'
                                  ? abrirModalCierreCtsDesdeLista(documento)
                                  : activeTab === 'gratificaciones'
                                    ? abrirModalCierreGratDesdeLista(documento)
                                    : handleCerrarDocumento(documento.id, 'liquidacion')
                              }
                              className="px-3 py-1 text-xs font-medium rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                            >
                              Cerrar
                            </button>
                          )}
                          {documento.estado === 'cerrada' && (
                            <button
                              onClick={() => handleAbrirDocumento(documento.id, activeTab === 'gratificaciones' ? 'gratificacion' : activeTab === 'cts' ? 'cts' : 'liquidacion')}
                              className="px-3 py-1 text-xs font-medium rounded-lg bg-yellow-50 text-yellow-700 hover:bg-yellow-100 transition-colors"
                            >
                              Abrir
                            </button>
                          )}
                          {activeTab === 'gratificaciones' && documento.estado === 'proceso' && (
                            <button
                              onClick={() => handleBorrarGratificacion(documento.id, documento.nombre)}
                              className="px-3 py-1 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                            >
                              Borrar
                            </button>
                          )}
                          {activeTab === 'cts' && documento.estado === 'proceso' && (
                            <button
                              onClick={() => handleBorrarCts(documento.id, documento.nombre)}
                              className="px-3 py-1 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                            >
                              Borrar
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Modal Nueva Gratificación */}
      {showModalNuevaGratificacion && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => !creandoGratificacion && setShowModalNuevaGratificacion(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Nuevo cálculo de gratificación</h3>
              {errorGratificacion && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {errorGratificacion}
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Período</label>
                  <select
                    value={nuevaGratificacionPeriodo}
                    onChange={(e) => setNuevaGratificacionPeriodo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Junio">Junio (Fiestas Patrias)</option>
                    <option value="Diciembre">Diciembre (Navidad)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Año</label>
                  <select
                    value={nuevaGratificacionAnio}
                    onChange={(e) => setNuevaGratificacionAnio(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {[new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModalNuevaGratificacion(false);
                    setErrorGratificacion(null);
                  }}
                  disabled={creandoGratificacion}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleCrearGratificacion}
                  disabled={creandoGratificacion}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50"
                >
                  {creandoGratificacion ? 'Creando...' : 'Crear'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CerrarCtsConfirmModal
        open={!!ctsCierreDesdeLista}
        subtitulo={ctsCierreDesdeLista?.nombre}
        submitting={ctsCierreEnviandoLista}
        onCancel={() => !ctsCierreEnviandoLista && setCtsCierreDesdeLista(null)}
        onConfirm={handleConfirmarCierreCtsDesdeLista}
      />

      <CerrarGratificacionConfirmModal
        open={!!gratCierreDesdeLista}
        subtitulo={gratCierreDesdeLista?.nombre}
        submitting={gratCierreEnviandoLista}
        onCancel={() => !gratCierreEnviandoLista && setGratCierreDesdeLista(null)}
        onConfirm={handleConfirmarCierreGratDesdeLista}
      />

      {showModalNuevaCts && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => !creandoCts && setShowModalNuevaCts(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Nuevo cálculo de CTS</h3>
              {errorCts && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {errorCts}
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Depósito</label>
                  <select
                    value={nuevaCtsPeriodo}
                    onChange={(e) => setNuevaCtsPeriodo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Mayo">Mayo (semestre nov–abr)</option>
                    <option value="Noviembre">Noviembre (semestre may–oct)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Año</label>
                  <select
                    value={nuevaCtsAnio}
                    onChange={(e) => setNuevaCtsAnio(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {[new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2].map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModalNuevaCts(false);
                    setErrorCts(null);
                  }}
                  disabled={creandoCts}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleCrearCts}
                  disabled={creandoCts}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50"
                >
                  {creandoCts ? 'Creando...' : 'Crear'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calculos;
